import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { backendCall } from "@/shared/services/backendService";

type AnnouncementItem = {
  _id: string;
  title: string;
  mlBriefDescription?: {
    en?: string;
    es?: string;
  };
  startDate?: string;
  endDate?: string;
  linkUrl?: string;
};

const ROTATE_INTERVAL_MS = 5000;
const ANIMATION_DURATION_MS = 600;

const AnnouncementBar = () => {
  const { i18n } = useTranslation();
  const [announcements, setAnnouncements] = useState<AnnouncementItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState<number | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchAnnouncements = async () => {
      const response = await backendCall({
        url: "/api/admin/announcements",
        method: "GET",
        contentType: "application/json",
        isShowErrorMessage: false,
      });

      if (!isMounted || !response?.success) return;

      const items = response?.data?.data;
      if (!Array.isArray(items)) return;

      const now = new Date();
      const activeItems = items.filter((item: AnnouncementItem) => {
        if (!item?.startDate || !item?.endDate) return false;
        const start = new Date(item.startDate);
        const end = new Date(item.endDate);
        return start <= now && end >= now;
      });

      setAnnouncements(activeItems);
      setCurrentIndex(0);
    };

    fetchAnnouncements();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (announcements.length <= 1) return;

    const intervalId = window.setInterval(() => {
      if (isAnimating) return;
      const targetIndex = (currentIndex + 1) % announcements.length;
      setNextIndex(targetIndex);
      setIsAnimating(true);
    }, ROTATE_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [announcements.length, currentIndex, isAnimating]);

  useEffect(() => {
    if (!isAnimating || nextIndex === null) return;

    const timeoutId = window.setTimeout(() => {
      setCurrentIndex(nextIndex);
      setNextIndex(null);
      setIsAnimating(false);
    }, ANIMATION_DURATION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isAnimating, nextIndex]);

  const currentAnnouncement = useMemo(
    () => announcements[currentIndex],
    [announcements, currentIndex]
  );
  const upcomingAnnouncement = useMemo(
    () => (nextIndex === null ? null : announcements[nextIndex]),
    [announcements, nextIndex]
  );

  if (!currentAnnouncement) return null;

  const language = (i18n.language || "es").startsWith("en") ? "en" : "es";
  const getDescription = (announcement?: AnnouncementItem | null) =>
    announcement?.mlBriefDescription?.[language] ||
    announcement?.mlBriefDescription?.en ||
    announcement?.mlBriefDescription?.es ||
    "";

  const currentDescription = getDescription(currentAnnouncement);
  const nextDescription = getDescription(upcomingAnnouncement);

  return (
    <div className="bg-lightBlue text-white absolute top-20 sm:top-16 w-100 z-50 pointer-events-auto shadow-md">
      <div className="container py-2 sm:min-h-[44px] min-h-[32px]" >
        <div className="position-relative overflow-hidden sm:min-h-[32px] min-h-[24px]">
          <div
            className="position-absolute top-0 start-0 w-100 text-darkBlue text-sm sm:text-base flex justify-center items-center gap-2 text-center text-md-start pointer-events-auto"
            style={{
              transform: isAnimating ? "translateY(100%)" : "translateY(0%)",
              opacity: isAnimating ? 0 : 1,
              transition: `transform ${ANIMATION_DURATION_MS}ms ease, opacity ${ANIMATION_DURATION_MS}ms ease`,
            }}
          >
            <span>{currentDescription}</span>
            {currentAnnouncement.linkUrl ? (
              <a
                href={currentAnnouncement.linkUrl}
                target="_blank"
                rel="noreferrer"
                className="text-darkBlue text-decoration-underline font-medium bg-[#d7e4fc] hover:bg-[#cddcfa] px-2 py-1 rounded"
              >
                Claim Now
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementBar;
